import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { analyticsService } from '../services/analyticsService';
import { GoogleLogin } from '@react-oauth/google';
import '../styles/HomePage.css';
import '../styles/Login.css';
import '../index.css';

function LoginPage() {
  const [view, setView] = useState('login');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [npUser, setNpUser] = useState(null);
  const [npPassword, setNpPassword] = useState('');

  const location = useLocation();

  // Set message from location state if available
  useEffect(() => {
    console.log("Current origin:", window.location.origin);
    console.log("Google Client ID:", process.env.REACT_APP_GOOGLE_CLIENT_ID);
  }, [location.state]);

  // Password validation
  const hasMinLength = newPassword.length >= 8 || loginPassword.length >= 8;
  const hasLowercase = /[a-z]/.test(newPassword) || /[a-z]/.test(loginPassword);
  const hasUppercase = /[A-Z]/.test(newPassword) || /[A-Z]/.test(loginPassword);
  const hasNumber = /\d/.test(newPassword) || /\d/.test(loginPassword);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword) || /[!@#$%^&*(),.?":{}|<>]/.test(loginPassword);
  
  const isPasswordValid = hasMinLength && hasLowercase && hasUppercase && hasNumber && hasSpecialChar;

  const { 
    login, 
    signUp, 
    verifyEmail, 
    forgotPassword, 
    confirmPassword,
    resendVerificationCode,
    loginWithGoogle,
    completeNewPassword
  } = useAuth();
  
  const navigate = useNavigate();

  const setErrorAndScroll = (errorMessage) => {
    setError(errorMessage);
    window.scrollTo(0, 0);
  };

  // Handle Google login success
  const handleGoogleSuccess = async (response) => {
    setGoogleLoading(true);
    try {
      console.log("Google response:", response);
      
      // Make sure we have a credential
      if (!response || !response.credential) {
        setError("Google login failed: No credential received");
        return;
      }
      
      // Call your auth function with the token
      await loginWithGoogle(response.credential);
      // Navigate on success
      navigate(location.state?.from || '/');
    } catch (err) {
      console.error("Google login error:", err);
      setError(err.message || "Failed to login with Google");
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const result = await login(loginEmail, loginPassword);

      if (result?.challenge === 'NEW_PASSWORD_REQUIRED') {
        setNpUser(result.cognitoUser);
        setView('newPasswordRequired');
        return;
      }

      analyticsService.trackEvent(loginEmail, 'login');
      const from = location.state?.from || '/research';
      navigate(from, { replace: true });
    } catch (err) {
      if (err.code === 'UserNotConfirmedException') {
        setMessage('Please verify your email first.');
        setView('verify');
      } else {
        setErrorAndScroll(err.message || 'Failed to login');
      }
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    
    try {
      await forgotPassword(forgotPasswordEmail);
      setMessage('If an account exists with this email, a password reset code will be sent.');
      setView('resetPassword');
    } catch (err) {
      // Don't expose whether the user exists or not for security
      setErrorAndScroll('Something went wrong. Please try again.');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      await confirmPassword(forgotPasswordEmail, resetCode, newPassword);
      setMessage('Password reset successful! You can now login.');
      setView('login');
      // Clear sensitive fields
      setResetCode('');
      setNewPassword('');
      setForgotPasswordEmail('');
    } catch (err) {
      setErrorAndScroll(err.message || 'Failed to reset password');
    }
  };

  const renderPasswordRequirements = (password) => {
    const currentPassword = view === 'resetPassword' ? newPassword : loginPassword;
    if (!currentPassword) return null;
    
    const requirements = [
      { met: currentPassword.length >= 8, text: "At least 8 characters" },
      { met: /[a-z]/.test(currentPassword), text: "One lowercase letter" },
      { met: /[A-Z]/.test(currentPassword), text: "One uppercase letter" },
      { met: /\d/.test(currentPassword), text: "One number" },
      { met: /[!@#$%^&*(),.?":{}|<>]/.test(currentPassword), text: "One special character" }
    ];
    
    return (
      <div className="container" style={{ padding: '20px', textAlign: 'left', margin: 0 }}>
        <h4>Password Requirements:</h4>
        <ul style={{ listStyleType: 'none', padding: 0 }}>
          {requirements.map((req, index) => (
            <li key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
              <span style={{ 
                color: req.met ? 'green' : 'red', 
                marginRight: '0.5rem',
                fontWeight: 'bold'
              }}>
                {req.met ? '✓' : '✗'}
              </span>
              {req.text}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderForgotPassword = () => (
    <div className="login-container">
      <h1>Reset Password</h1>
      <p>Enter your email to receive a password reset code.</p>
      
      <form onSubmit={handleForgotPassword}>
        <div className="form-group">
          
          <input
            type="email"
            id="forgotPasswordEmail"
            value={forgotPasswordEmail}
            onChange={(e) => setForgotPasswordEmail(e.target.value)}
            required
            placeholder="E-mail"
          />
        </div>
        
        <button type="submit" className="button standout-button login-btn" style={{ maxWidth: '15rem', marginBottom: '0.5rem' }}>
          Send Reset Code
        </button>
        
        <div className="register-prompt">
          <p>
            <span 
              className="register-link" 
              onClick={() => setView('login')}
            >
              Back to Login
            </span>
          </p>
        </div>
      </form>
    </div>
  );

  const renderResetPassword = () => (
    <div className="login-container">
      <h1>Reset Your Password</h1>
      <p>A reset code has been sent to: <strong>{forgotPasswordEmail}</strong></p>
      
      <form onSubmit={handleResetPassword}>
        <div className="form-group" style={{maxWidth: "80%"}}>
          <label htmlFor="resetCode">Reset Code</label>
          <div className='reset-code-container'>
            <input
            type="text"
            id="resetCode"
            
            value={resetCode}
            onChange={(e) => setResetCode(e.target.value)}
            required
            placeholder="Enter the code from your email"
            />
          </div>
          
          <label htmlFor="newPassword">New Password</label>
          <div className="password-input-container">
            <input
              type={showNewPassword ? "text" : "password"}
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              placeholder="Create a secure password"
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="toggle-password-btn"
              title={showNewPassword ? "Hide password" : "Show password"}
            >
              {showNewPassword ? '🙉' : '🙈'}
            </button>
          </div>
          
          {renderPasswordRequirements(newPassword)}
        </div>
        
        <button 
          type="submit" 
          className="button standout-button login-btn"
          disabled={!isPasswordValid} 
          style={{ marginTop: '1rem', maxWidth: '15rem' }}
        >
          Reset Password
        </button>
        
        <div className="register-prompt">
          <p>
            <span 
              className="register-link" 
              onClick={() => setView('forgotPassword')}
            >
              ← Back to previous step
            </span>
          </p>
        </div>
      </form>
    </div>
  );

  const renderNewPassword = () => (
    <div className="login-container">
      <h1>Complete Your Account Setup</h1>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setError('');
          try {
            // Include user attributes with the password challenge
            await completeNewPassword(npUser, npPassword, {
              'given_name': firstName,
              'family_name': lastName
            });
            navigate(location.state?.from || '/research', { replace: true });
          } catch (err) {
            setErrorAndScroll(err.message || 'Failed to set new password');
          }
        }}
      >
        <div className="form-group">
          <label htmlFor="firstName">First Name</label>
          <input
            type="text"
            id="firstName"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            placeholder="Enter your first name"
          />
          
          <label htmlFor="lastName">Last Name</label>
          <input
            type="text"
            id="lastName"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            placeholder="Enter your last name"
          />
          
          <label htmlFor="newPassword">New Password</label>
          <div className="password-input-container">
            <input
              type={showNewPassword ? "text" : "password"}
              value={npPassword}
              onChange={(e) => setNpPassword(e.target.value)}
              required
              placeholder="Create a new password"
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="toggle-password-btn"
            >
              {showNewPassword ? '🙉' : '🙈'}
            </button>
          </div>
        </div>
        <button type="submit" className="button standout-button login-btn">
          Complete Setup
        </button>
      </form>
    </div>
  );

  const renderLogin = () => (
    <div className="login-container">
      <h1>Welcome!</h1>
      
      <div className="google-login-container">
        <GoogleLogin
          onSuccess={handleGoogleSuccess}
          onError={() => setError("Google Sign-In failed")}
          useOneTap={false}
          text="signin_with"
          disabled={googleLoading}
        />
        {googleLoading && (
          <div className="loading-indicator">
            <span>Logging you in with Google...</span>
          </div>
        )}
      </div>
      
      <div className="divider">
        <span>or login with email</span>
      </div>
      
      <form onSubmit={handleLogin}>
        <div className="form-group">
          
          <input 
            type="email" 
            id="loginEmail" 
            value={loginEmail} 
            onChange={(e) => setLoginEmail(e.target.value)} 
            required 
            placeholder="E-mail adress"
          />
          
          <div className="password-input-container">
            <input 
              type={showPassword ? "text" : "password"} 
              id="loginPassword" 
              value={loginPassword} 
              onChange={(e) => setLoginPassword(e.target.value)} 
              required 
              placeholder="Password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="toggle-password-btn"
              title={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? '🙉' : '🙈'}
            </button>
          </div>
        </div>
        
        <div className="forgot-password">
          <Link onClick={() => setView('forgotPassword')}>
            Forgot Password?
          </Link>
        </div>
        
        <button type="submit" className="button standout-button login-btn" style={{ marginBottom: '0' }}>
          Login
        </button>
      </form>
  
      <div className="register-prompt">
        <p style={{ fontSize: '1rem' }}>
           <span 
            className="register-link" 
            onClick={() => setView('register')}
          >
            Register here
          </span>
        </p>
      </div>
    </div>
  );

  const renderRegister = () => (
    <div className="login-container" >
      <h1 style={{marginBottom: "0"}}>Create Account</h1>
      <form onSubmit={handleRegister}>
        <div className="form-group">
          <label htmlFor="firstName">First Name*</label>
          <input 
            type="text" 
            id="firstName" 
            value={firstName} 
            onChange={(e) => setFirstName(e.target.value)} 
            required 
            placeholder="Enter your first name"
          />
          
          <label htmlFor="lastName">Last Name*</label>
          <input 
            type="text" 
            id="lastName" 
            value={lastName} 
            onChange={(e) => setLastName(e.target.value)} 
            required 
            placeholder="Enter your last name"
          />
          
          <label htmlFor="loginEmail">Email*</label>
          <input 
            type="email" 
            id="loginEmail" 
            value={loginEmail} 
            onChange={(e) => setLoginEmail(e.target.value)} 
            required 
            placeholder="Enter your email address"
          />
          
          <label htmlFor="loginPassword">Password*</label>
          <div className="password-input-container">
            <input 
              type={showPassword ? "text" : "password"} 
              id="loginPassword" 
              value={loginPassword} 
              onChange={(e) => setLoginPassword(e.target.value)} 
              required 
              placeholder="Create a secure password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="toggle-password-btn"
              title={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? '🙉' : '🙈'}
            </button>
          </div>
          
          {renderPasswordRequirements(loginPassword)}
        </div>
        
        <button 
          type="submit" 
          className="button standout-button login-btn"
          disabled={!isPasswordValid && loginPassword.length > 0}
        >
          Register
        </button>
        
        <div className="register-prompt">
          <p>
            Already have an account?{' '}
            <span className="register-link" onClick={() => setView('login')}>
              Login here
            </span>
          </p>
        </div>
      </form>
    </div>
  );

  const handleRegister = async (event) => {
    event.preventDefault();
    setError('');
    
    try {
      await signUp(loginEmail, loginPassword, firstName, lastName);
      analyticsService.trackEvent(loginEmail, 'signup');
      setMessage('Registration successful! Please check your email for verification code.');
      setView('verify');
    } catch (err) {
      if (err.code === 'UsernameExistsException') {
        try {
          await resendVerificationCode(loginEmail);
          setMessage('Account exists but not verified. A new verification code has been sent.');
          setView('verify');
        } catch (resendErr) {
          setErrorAndScroll('Error resending verification code. Please try again.');
        }
      } else {
        setErrorAndScroll(err.message || 'Failed to register');
      }
    }
  };

  const renderVerification = () => (
    <div className="login-container">
      <h1>Verify Email</h1>
      <p>A verification code has been sent to: <strong>{loginEmail}</strong></p>
      <form onSubmit={handleVerification}>
        <div className="form-group">
          <input 
            type="text" 
            id="verificationCode" 
            value={verificationCode} 
            onChange={(e) => setVerificationCode(e.target.value)} 
            required 
            placeholder="Enter the code from your email"
          />
        </div>
        
        <button type="submit" className="button standout-button login-btn" style={{maxWidth:'20rem'}}>
          Verify Email
        </button>
        
        <div className="register-prompt">
          <p>
            <span 
              className="register-link" 
              onClick={handleResendCode}
            >
              Resend Code
            </span>
            {' | '}
            <span 
              className="register-link" 
              onClick={() => setView('login')}
            >
              Back to Login
            </span>
          </p>
        </div>
      </form>
    </div>
  );
  
  const handleVerification = async (event) => {
    event.preventDefault();
    setError('');
    
    try {
      await verifyEmail(loginEmail, verificationCode);
      
      // Send welcome email after successful verification
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/user/send-welcome-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: loginEmail })
        });
        
        if (!response.ok) {
          console.error('Failed to send welcome email');
        }
      } catch (emailErr) {
        console.error('Error sending welcome email:', emailErr);
        // Don't block the user if welcome email fails
      }
      
      setMessage('Email verified successfully! You can now login.');
      setView('login');
    } catch (err) {
      setErrorAndScroll(err.message || 'Failed to verify email');
    }
  };

  const handleResendCode = async () => {
    try {
      await resendVerificationCode(loginEmail);
      setMessage('Verification code resent! Please check your email.');
    } catch (err) {
      setErrorAndScroll(err.message || 'Failed to resend code');
    }
  };

  return (
    <div className="login-page" style={{ minHeight: '90vh' }}>
      <div className="hero-1 top-hero" style={{ paddingTop: 'calc(4vh + 2rem)' }}>
          {error && <div className="error-message">{error}</div>}
          {message && <div className="success-message" style={{ marginTop: '10rem !important' }}>{message}</div>}
          
          {view === 'login' && renderLogin()}
          {view === 'register' && renderRegister()}
          {view === 'verify' && renderVerification()}
          {view === 'forgotPassword' && renderForgotPassword()}
          {view === 'resetPassword' && renderResetPassword()}
          {view === 'newPasswordRequired' && renderNewPassword()}
        </div>
      </div>
  );
}

export default LoginPage;